import { AdaptivePagination } from "@/components/flexkit/AdaptivePagination";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { AddGroupMembersDialog } from "@/components/settings/organization/groups/AddGroupMembersDialog";
import { EditGroupDialog } from "@/components/settings/organization/groups/EditGroupDialog";
import { GroupCard } from "@/components/settings/organization/groups/GroupCard";
import { GroupsTableBody, type RoleAssignmentsData } from "@/components/settings/organization/groups/GroupsTable";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Group } from "gitpod-next-api/gitpod/v1/group_pb";
import { useState, useMemo, useEffect, type FC } from "react";

const GROUPS_PAGE_SIZE = 25;

type GroupsListProps = {
    groups: PlainMessage<Group>[];
    orgRolesEnabled: boolean;
    isLoading?: boolean;
    roleAssignments?: RoleAssignmentsData;
};

export const GroupsList: FC<GroupsListProps> = ({ groups, orgRolesEnabled, isLoading, roleAssignments }) => {
    const [searchValue, setSearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [showAddMembersDialog, setShowAddMembersDialog] = useState<{ group: PlainMessage<Group> } | undefined>();
    const [showEditDialog, setShowEditDialog] = useState<{ group: PlainMessage<Group> } | undefined>();

    const filteredGroups = useMemo(() => {
        // Filter out system-managed groups and direct share groups
        const userGroups = groups.filter((group) => !group.systemManaged && !group.directShare);

        if (!searchValue.trim()) return userGroups;

        const query = searchValue.toLowerCase();
        return userGroups.filter((group) => group.name.toLowerCase().includes(query));
    }, [groups, searchValue]);

    const totalPages = Math.ceil(filteredGroups.length / GROUPS_PAGE_SIZE);

    // Reset to first page when search changes
    useEffect(() => {
        setCurrentPage(0);
    }, [searchValue]);

    // Clamp page if filtered results shrink
    useEffect(() => {
        if (currentPage >= totalPages && totalPages > 0) {
            setCurrentPage(totalPages - 1);
        }
    }, [currentPage, totalPages]);

    const paginatedGroups = useMemo(
        () => filteredGroups.slice(currentPage * GROUPS_PAGE_SIZE, (currentPage + 1) * GROUPS_PAGE_SIZE),
        [filteredGroups, currentPage],
    );

    return (
        <div>
            {/* Search Input */}
            <SearchInput
                wrapperClassName="w-full"
                className="max-w-none"
                placeholder="Search by name or keyword"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onClear={() => setSearchValue("")}
                data-tracking-id="groups-search"
            />

            {/* Groups view — table when org roles enabled, card grid otherwise */}
            {orgRolesEnabled ? (
                <>
                    <GroupsTableBody
                        groups={paginatedGroups}
                        searchValue={searchValue}
                        isLoading={isLoading}
                        roleAssignments={roleAssignments}
                        onAddMembers={(group) => setShowAddMembersDialog({ group })}
                        onEdit={(group) => setShowEditDialog({ group })}
                    />
                    {totalPages > 1 && (
                        <AdaptivePagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={filteredGroups.length}
                            pageSize={GROUPS_PAGE_SIZE}
                            currentItemsCount={paginatedGroups.length}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </>
            ) : (
                <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4">
                    {filteredGroups.map((group) => (
                        <GroupCard
                            key={group.id}
                            group={group}
                            onAddMembers={() => setShowAddMembersDialog({ group })}
                            onEdit={() => setShowEditDialog({ group })}
                        />
                    ))}
                </div>
            )}

            {/* Add group members */}
            {showAddMembersDialog?.group && (
                <AddGroupMembersDialog
                    open={true}
                    onClose={() => setShowAddMembersDialog(undefined)}
                    group={showAddMembersDialog.group}
                />
            )}

            {/* Edit group */}
            {showEditDialog?.group && (
                <EditGroupDialog
                    open={true}
                    onClose={() => setShowEditDialog(undefined)}
                    group={showEditDialog.group}
                />
            )}
        </div>
    );
};
